import {startTransition, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getHSectionVO,
    saveHSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    H2_PAGE,
    HS_PAGE,
    H_PAGE_TITLES,
    H_SECTION_INDEX,
    H_SECTION_INDEX_LETTER,
    H_SECTION_INDEX_TITLE,
    H_SECTION_PAGES,
    HProgrammePage,
    HSectionPageCode,
    HSummaryPage,
} from '@/pages/components/audit-program/h-section/constants';
import HsSummaryView from '@/pages/components/audit-program/h-section/HsSummaryView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type HSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: HSectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<HSectionPageCode>(['Hi']);

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function HSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Hi',
}: HSectionViewProps) {
    const [pageKey, setPageKey] = useState<HSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('H');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getHSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of H_SECTION_PAGES) {
                if (code === 'Hi') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load H Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: HSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveHSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('Hi') as {rows?: typeof H_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : H_SECTION_INDEX;
    }, [getContent]);

    const hsContent = (getContent('Hs') ?? HS_PAGE) as HSummaryPage;
    const h2Content = (getContent('H2') ?? H2_PAGE) as HProgrammePage;

    const tabs = useMemo(() => H_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={H_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Hi' ? (
                <SectionIndexView
                    header={header}
                    refCode="Hi"
                    sectionLetter={H_SECTION_INDEX_LETTER}
                    sectionTitle={H_SECTION_INDEX_TITLE}
                    rows={indexRows}
                    onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as HSectionPageCode))}
                />
            ) : code === 'Hs' ? (
                <HsSummaryView
                    header={header}
                    page={hsContent}
                    refCode="Hs"
                    values={pageData.Hs ?? {}}
                    onChange={(k, v) => handleFieldChange('Hs', k, v)}
                />
            ) : (
                <G0ProgrammeView
                    header={header}
                    page={h2Content}
                    refCode="H2"
                    values={pageData.H2 ?? {}}
                    onChange={(k, v) => handleFieldChange('H2', k, v)}
                />
            )
        ) : null,
    })), [header, pageData, handleFieldChange, indexRows, hsContent, h2Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    H Section — Other investments
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as HSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
